import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getFeaturedBuildings } from "../data/buildings";
import BuildingCard from "../components/common/BuildingCard";
import EnquiryForm from "../components/common/EnquiryForm";
import InstagramBanner from "../components/common/InstagramBanner";

const featuredBuildings = getFeaturedBuildings();

const carouselSlides = [
  {
    title: "Space to Inspire",
    text: "Artistic Spaces affordable workspaces are perfect for new, growing or established businesses. You don't have to be an artist, but we feel that our workspaces will bring out the creative in you. Although in your own space you'll be surrounded by other talented and creative minds and craftspeople and that can only be a good thing. Our workspaces are easy to let, the contracts are easy to understand, so you can breathe easy and concentrate on doing what you love, in a space that works for you.",
    link: "/about-us",
    linkLabel: "FIND OUT MORE",
    image: "/static/images/home-offset-1.webp",
    imageLeft: false,
  },
  {
    title: "Space to Grow",
    text: "We now have 19 creative complexes with over 700 individual units across London.",
    link: "/our-locations",
    linkLabel: "FIND OUT MORE",
    image: "/static/images/home-offset-2.webp",
    imageLeft: true,
  },
  {
    title: "Space to Work",
    text: "When you take some space at one of our sites, you'll be dealing with us, just us, nobody else and certainly not any agents. And hey, doesn't that make things easier? Take a good look at the site that you are interested in on this website, it has up-to-date information about the property and the currently available space. If you see something you like or have any questions, get in touch on 0203 302 4266 or use the contact form at the foot of the page. We'll chat through your needs, answer your questions and in some instances even get you in on the same day.",
    link: "/contact",
    linkLabel: "FIND OUT MORE",
    image: "/static/images/home-offset-3.webp",
    imageLeft: false,
  },
];

function Carousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % carouselSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const slide = carouselSlides[current];

  return (
    <section className="w-full bg-[#f0ede6]">
      <div className={`flex flex-col ${slide.imageLeft ? "md:flex-row" : "md:flex-row-reverse"} min-h-[500px]`}>
        <div className="w-full md:w-1/2 min-h-[320px] md:min-h-[500px] overflow-hidden">
          <img key={slide.image} src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
        </div>
        <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12">
          <div className="max-w-sm">
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">{slide.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-6 text-base">{slide.text}</p>
            <Link to={slide.link} className="inline-flex items-center gap-2 text-sm font-semibold text-[#8a7d5a] tracking-widest uppercase hover:gap-4 transition-all duration-200">
              {slide.linkLabel} <span>→</span>
            </Link>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-2 py-5">
        {carouselSlides.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} className={`h-2 rounded-full transition-all duration-200 ${i === current ? "bg-gray-800 w-6" : "bg-gray-400 w-2"}`} aria-label={`Go to slide ${i + 1}`} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <section
        className="relative w-full h-screen min-h-[560px] flex flex-col items-center justify-center"
        style={{ backgroundImage: "url('/static/images/hero-home.webp')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-semibold mb-4">Welcome to Artistic Spaces</h1>
          <p className="text-lg md:text-xl max-w-xl mx-auto mb-10 text-white/90">
            Here to help you find an affordable workspace in London, within a community near you
          </p>
          <div className="w-16 h-px bg-white mx-auto mb-6" />
          <Link to="/our-locations" className="inline-block text-sm font-semibold text-white tracking-widest uppercase border-b border-white pb-0.5 hover:opacity-70 transition-opacity">
            OUR LOCATIONS
          </Link>
        </div>
      </section>

      {/* Practical */}
      <section className="w-full bg-[#f0ede6]">
        <div className="flex flex-col md:flex-row min-h-[500px]">
          <div className="w-full md:w-1/2 flex items-center px-8 md:px-16 lg:px-24 py-12 order-2 md:order-1">
            <div className="max-w-sm">
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-5">Practical</h2>
              <p className="text-gray-600 leading-relaxed mb-6 text-base">
                Our workspaces are in great locations, offering flexible and affordable solutions for everyone. From after-work hobbyists to tech startups and growing businesses.
              </p>
              <Link to="/about-us" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8a7d5a] tracking-widest uppercase hover:gap-4 transition-all duration-200">
                FIND OUT MORE <span>→</span>
              </Link>
            </div>
          </div>
          <div className="w-full md:w-1/2 min-h-[320px] md:min-h-[500px] overflow-hidden order-1 md:order-2">
            <img src="/static/images/home-intro-pic.webp" alt="Practical workspace" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Featured Locations */}
      <section className="w-full py-16 md:py-24 px-4 sm:px-8 lg:px-16">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">Featured Locations</h2>
        <p className="text-gray-600 max-w-3xl mb-3 leading-relaxed text-base">
          At Artistic Spaces, we provide a diverse range of inspiring workspaces across London for businesses, creatives, and individuals. Whether you're a budding photographer, an independent baker, a freelance designer, or scaling a fast-growing tech company, we have the perfect space to support your ambitions.
        </p>
        <p className="text-gray-600 max-w-3xl mb-3 leading-relaxed text-base">
          With secure 24-hour access and ultra-fast, business-grade Wi-Fi (including upgraded 5G connectivity at select sites), our nineteen locations offer the perfect environment to thrive.
        </p>
        <Link to="/our-locations" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8a7d5a] tracking-widest uppercase mb-12 hover:gap-4 transition-all duration-200">
          VIEW LOCATION MAP <span>→</span>
        </Link>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
          {featuredBuildings.map((building) => (
            <BuildingCard key={building.slug} building={building} />
          ))}
        </div>
      </section>

      <Carousel />
      <InstagramBanner />
      <EnquiryForm pageUri="https://artistic-spaces.co.uk/" pageName="Homepage" />
    </main>
  );
}